import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { Hourglass, Play, VideoOff } from 'lucide-react';
import { cn } from '../lib/cn.js';
/** Ширина превью; высота — по 9:16. `xs` 40 px — строка таблицы (как в старой админке). */
const sizeClass = {
    xs: 'w-10',
    sm: 'w-16',
    md: 'w-30',
    lg: 'w-45',
};
export const VIDEO_THUMB_UNAVAILABLE_LABEL_RU = 'превью нет';
export const VIDEO_THUMB_UNAVAILABLE_HINT_RU = 'Файл видео недоступен на сервере';
export const VIDEO_THUMB_PREPARING_LABEL_RU = 'Видео готовится';
const DEFAULT_PREPARING_RETRY_MS = 30_000;
const DEFAULT_PREPARING_MAX_RETRIES = 6;
/**
 * Превью видео 9:16: постер; **наведение (или фокус с клавиатуры) — видео
 * играет сразу, без звука, по кругу; уход — пауза и перемотка в начало**;
 * клик — `onOpen` (полный просмотр в `VideoPlayerModal`).
 *
 * Почему `<video>` монтируется лениво, а не `preload="none"` на всех: список
 * из 200 клипов с `<video src>` даже без предзагрузки в части браузеров тянет
 * метаданные каждого. Здесь до наведения в DOM есть только `<img
 * loading="lazy">` постера; после первого наведения `<video preload="none">`
 * остаётся смонтированным, чтобы повторное наведение не качало заново.
 *
 * Ошибка видео (404/битый файл) — заглушка «превью нет» и больше никаких
 * попыток; ошибка постера — нейтральный фон, наведение по-прежнему играет.
 */
export const VideoThumb = React.forwardRef(({ src, poster, label, onOpen, size = 'sm', playOnHover = true, preparing = false, retryMs = DEFAULT_PREPARING_RETRY_MS, maxRetries = DEFAULT_PREPARING_MAX_RETRIES, className, }, ref) => {
    const videoRef = React.useRef(null);
    const [armed, setArmed] = React.useState(false);
    const [playing, setPlaying] = React.useState(false);
    const [videoFailed, setVideoFailed] = React.useState(false);
    const [posterFailed, setPosterFailed] = React.useState(false);
    // «Готовится»: файл появился (постер загрузился) / номер попытки постера /
    // ждём повтора (запроса сейчас нет).
    const [ready, setReady] = React.useState(false);
    const [attempt, setAttempt] = React.useState(0);
    const [waiting, setWaiting] = React.useState(false);
    // Смена источника — новое видео: сбросить всё, что знали о старом.
    React.useEffect(() => {
        setArmed(false);
        setPlaying(false);
        setVideoFailed(false);
        setPosterFailed(false);
        setReady(false);
        setAttempt(0);
        setWaiting(false);
    }, [src, poster, preparing]);
    React.useEffect(() => {
        if (!waiting)
            return;
        const t = setTimeout(() => {
            setWaiting(false);
            setAttempt((a) => a + 1);
        }, retryMs);
        return () => clearTimeout(t);
    }, [waiting, retryMs]);
    const stillPreparing = preparing && !ready;
    const unavailable = src === null || videoFailed || stillPreparing;
    const canPlay = playOnHover && !unavailable;
    const start = () => {
        if (!canPlay)
            return;
        setArmed(true);
        setPlaying(true);
    };
    const stop = () => {
        setPlaying(false);
        const v = videoRef.current;
        if (!v)
            return;
        v.pause();
        try {
            v.currentTime = 0;
        }
        catch {
            // Некоторые браузеры бросают, пока метаданные не пришли — сброс не нужен.
        }
    };
    // Старт — после монтирования `<video>` (первое наведение) или сразу.
    React.useEffect(() => {
        if (!playing)
            return;
        const v = videoRef.current;
        if (!v)
            return;
        v.muted = true;
        const p = v.play();
        // Отказ автоплея (вкладка в фоне, политика браузера) — не ошибка.
        if (p && typeof p.catch === 'function')
            p.catch(() => { });
    }, [playing, armed]);
    const showVideo = armed && playing && !unavailable;
    const sizeCls = sizeClass[size];
    const big = size === 'md' || size === 'lg';
    // Повтор — новый адрес (`?retry=N`): тот же адрес браузер мог бы не
    // запросить снова после ошибки.
    const probeSrc = poster && attempt > 0
        ? `${poster}${poster.includes('?') ? '&' : '?'}retry=${attempt}`
        : poster;
    const media = stillPreparing ? (_jsxs("span", { className: "relative flex h-full w-full flex-col items-center justify-center gap-1 text-[var(--color-text-muted)]", title: VIDEO_THUMB_PREPARING_LABEL_RU, children: [probeSrc && !waiting && attempt <= maxRetries ? (_jsx("img", { src: probeSrc, alt: "", decoding: "async", draggable: false, onLoad: () => setReady(true), onError: () => {
                    if (attempt < maxRetries)
                        setWaiting(true);
                    else
                        setAttempt(maxRetries + 1);
                }, className: "absolute inset-0 h-full w-full object-cover opacity-0" }, attempt)) : null, _jsx(Hourglass, { "aria-hidden": true, className: big ? 'h-6 w-6' : 'h-4 w-4' }), big ? _jsx("span", { className: "text-xs", children: VIDEO_THUMB_PREPARING_LABEL_RU }) : null] })) : unavailable ? (_jsxs("span", { className: "flex h-full w-full flex-col items-center justify-center gap-1 text-[var(--color-text-muted)]", title: VIDEO_THUMB_UNAVAILABLE_HINT_RU, children: [_jsx(VideoOff, { "aria-hidden": true, className: big ? 'h-6 w-6' : 'h-4 w-4' }), big ? _jsx("span", { className: "text-xs", children: VIDEO_THUMB_UNAVAILABLE_LABEL_RU }) : null] })) : (_jsxs(_Fragment, { children: [poster && !posterFailed ? (_jsx("img", { src: poster, alt: "", loading: "lazy", decoding: "async", draggable: false, onError: () => setPosterFailed(true), className: "absolute inset-0 h-full w-full object-cover" })) : null, armed ? (_jsx("video", { ref: videoRef, src: src ?? undefined, muted: true, loop: true, playsInline: true, preload: "none", "aria-hidden": true, tabIndex: -1, onError: () => {
                    setVideoFailed(true);
                    setPlaying(false);
                }, className: cn('absolute inset-0 h-full w-full object-cover', showVideo ? 'opacity-100' : 'opacity-0') })) : null, big && !showVideo ? (_jsx("span", { "aria-hidden": true, className: "absolute bottom-1.5 left-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-scrim)] text-[var(--color-text-on-accent)]", children: _jsx(Play, { className: "h-3.5 w-3.5" }) })) : null] }));
    const frame = cn('relative inline-flex aspect-[9/16] shrink-0 overflow-hidden rounded-[var(--radius-3)] border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] align-middle', sizeCls, className);
    const hoverHandlers = {
        onMouseEnter: start,
        onMouseLeave: stop,
        onFocus: start,
        onBlur: stop,
    };
    if (onOpen && !unavailable) {
        return (_jsx("button", { ref: ref, type: "button", "aria-label": `Смотреть: ${label}`, "data-state": showVideo ? 'playing' : 'idle', onClick: onOpen, ...hoverHandlers, className: cn(frame, 'cursor-pointer p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)]'), children: media }));
    }
    return (_jsx("span", { ref: ref, role: "img", "aria-label": label, ...(stillPreparing ? { title: VIDEO_THUMB_PREPARING_LABEL_RU } : {}), "data-state": stillPreparing ? 'preparing' : showVideo ? 'playing' : 'idle', onMouseEnter: start, onMouseLeave: stop, className: frame, children: media }));
});
VideoThumb.displayName = 'VideoThumb';
//# sourceMappingURL=video-thumb.js.map